import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Plus, Trash2, ExternalLink, Users, Edit3, Save } from "lucide-react";

const EMPTY_JOB = {
  title: "", description: "", department: "", subject: "",
  employment_type: "full-time", work_mode: "on-site",
  location_city: "", location_state: "", location_country: "India",
  salary_min: null, salary_max: null, salary_currency: "INR", salary_period: "year",
  application_url: "", application_email: "", apply_deadline: "",
  employer_type: "university", employer_name: "", employer_logo: "", employer_website: "",
  is_featured: false, is_sponsored: false, status: "active",
};

export default function JobsOwnerPanel() {
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [f, setF] = useState(EMPTY_JOB);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/my/jobs").then(r => setJobs(r.data));
    api.get("/my/jobs-applications").then(r => setApplications(r.data));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        salary_min: f.salary_min ? Number(f.salary_min) : null,
        salary_max: f.salary_max ? Number(f.salary_max) : null,
      };
      if (editingId) {
        await api.put(`/my/jobs/${editingId}`, payload);
        toast.success("Job updated");
      } else {
        await api.post("/my/jobs", payload);
        toast.success("Job posted");
      }
      setCreating(false); setEditingId(null); setF(EMPTY_JOB); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const edit = (j) => {
    setEditingId(j.id); setCreating(true);
    setF({ ...EMPTY_JOB, ...j });
  };
  const del = async (id) => {
    if (!confirm("Delete this job posting?")) return;
    await api.delete(`/my/jobs/${id}`); toast.success("Deleted"); load();
  };

  if (creating) {
    return (
      <form onSubmit={submit} className="card-soft p-6 space-y-4" data-testid="job-form">
        <div className="flex items-center gap-2 text-sky-700 mb-2"><Briefcase className="w-4 h-4"/><span className="overline">{editingId ? "Edit job posting" : "Post a new job"}</span></div>
        <input required placeholder="Job title (e.g. Assistant Professor of Physics)" value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="job-title-input"/>
        <textarea required rows={8} placeholder="Full job description (HTML allowed)" value={f.description} onChange={e => setF({...f, description: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-mono text-sm" data-testid="job-desc-input"/>
        <div className="grid md:grid-cols-3 gap-3">
          <input placeholder="Subject / discipline" value={f.subject} onChange={e => setF({...f, subject: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input placeholder="Department" value={f.department} onChange={e => setF({...f, department: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <select value={f.employment_type} onChange={e => setF({...f, employment_type: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
            <option value="full-time">Full-time</option><option value="part-time">Part-time</option><option value="contract">Contract</option><option value="temporary">Temporary</option><option value="internship">Internship</option>
          </select>
          <select value={f.work_mode} onChange={e => setF({...f, work_mode: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
            <option value="on-site">On-site</option><option value="hybrid">Hybrid</option><option value="remote">Remote</option>
          </select>
          <input placeholder="City" value={f.location_city} onChange={e => setF({...f, location_city: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input placeholder="State" value={f.location_state} onChange={e => setF({...f, location_state: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input type="number" placeholder="Salary min (annual ₹)" value={f.salary_min || ""} onChange={e => setF({...f, salary_min: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input type="number" placeholder="Salary max (annual ₹)" value={f.salary_max || ""} onChange={e => setF({...f, salary_max: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input type="date" placeholder="Apply deadline" value={f.apply_deadline} onChange={e => setF({...f, apply_deadline: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input placeholder="Application URL (optional)" value={f.application_url} onChange={e => setF({...f, application_url: e.target.value})} className="md:col-span-2 px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
          <input placeholder="Contact email" value={f.application_email} onChange={e => setF({...f, application_email: e.target.value})} className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400"/>
        </div>
        <div className="flex gap-2">
          <button disabled={busy} className="px-6 py-3 rounded-full bg-sky-700 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="job-save-btn">
            {editingId ? <Save className="w-4 h-4"/> : <Plus className="w-4 h-4"/>}
            {busy ? "Saving…" : editingId ? "Save changes" : "Post Job"}
          </button>
          <button type="button" onClick={() => { setCreating(false); setEditingId(null); setF(EMPTY_JOB); }} className="px-4 py-3 rounded-full bg-slate-100 text-slate-700 text-sm">Cancel</button>
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-6" data-testid="jobs-owner-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">Your job postings</h3>
          <p className="text-sm text-slate-500 mt-1">{jobs.length} active posting{jobs.length !== 1 && "s"} · {applications.length} total application{applications.length !== 1 && "s"}</p>
        </div>
        <button onClick={() => { setCreating(true); setEditingId(null); setF(EMPTY_JOB); }} className="px-5 py-2.5 rounded-full bg-sky-700 text-white font-semibold text-sm flex items-center gap-1" data-testid="new-job-btn"><Plus className="w-4 h-4"/>Post a Job</button>
      </div>

      {jobs.length === 0 ? (
        <div className="card-soft p-12 text-center text-slate-500" data-testid="no-jobs">No jobs posted yet. Click "Post a Job" to start.</div>
      ) : (
        <div className="card-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
              <tr><th className="px-5 py-3">Title</th><th className="px-5 py-3">Location</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Applications</th><th className="px-5 py-3">Posted</th><th className="px-5 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="border-b border-slate-50 hover:bg-sky-50/30" data-testid={`job-row-${j.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-editorial font-semibold text-slate-900">{j.title}</div>
                    <div className="text-xs text-slate-500">{j.subject}</div>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-600">{[j.location_city, j.location_state].filter(Boolean).join(", ")}</td>
                  <td className="px-5 py-3 text-xs text-slate-600">{j.employment_type} · {j.work_mode}</td>
                  <td className="px-5 py-3 text-sm text-slate-800 font-semibold">{applications.filter(a => a.job_id === j.id).length}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{formatDate(j.posted_at)}</td>
                  <td className="px-5 py-3 text-right flex justify-end gap-1">
                    <Link to={`/job/${j.slug}`} target="_blank" className="p-2 rounded-lg bg-slate-100 hover:bg-sky-100 text-slate-600" data-testid={`job-view-${j.id}`}><ExternalLink className="w-4 h-4"/></Link>
                    <button onClick={() => edit(j)} className="p-2 rounded-lg bg-slate-100 hover:bg-sky-100 text-slate-600" data-testid={`job-edit-${j.id}`}><Edit3 className="w-4 h-4"/></button>
                    <button onClick={() => del(j.id)} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" data-testid={`job-del-${j.id}`}><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {applications.length > 0 && (
        <div>
          <h3 className="font-editorial text-xl font-bold text-slate-900 mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-sky-700"/>Applications</h3>
          <div className="card-soft overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70 border-b border-slate-100 text-left">
                <tr><th className="px-5 py-3">Applicant</th><th className="px-5 py-3">Applied for</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">CV</th><th className="px-5 py-3">Received</th></tr>
              </thead>
              <tbody>
                {applications.slice(0, 30).map(a => (
                  <tr key={a.id} className="border-b border-slate-50">
                    <td className="px-5 py-3 font-editorial font-semibold text-slate-900">{a.name}</td>
                    <td className="px-5 py-3 text-xs text-slate-600">{a.job_title}</td>
                    <td className="px-5 py-3 text-xs"><a href={`mailto:${a.email}`} className="text-sky-700">{a.email}</a></td>
                    <td className="px-5 py-3 text-xs">{a.cv_url ? <a href={a.cv_url} target="_blank" rel="noreferrer" className="text-sky-700">View</a> : "—"}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{formatDate(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
